<template>
  <el-dialog
    title="Add new choice template"
    width="600px"
    :before-close="handleCancel"
    :visible.sync="isVisible"
  >
    <el-form
      label-position="right"
      label-width="150px"
      ref="templateForm"
      :model="templateForm"
      :rules="rules"
    >
      <el-form-item label="Template name" prop="title">
        <el-input v-model="templateForm.title"></el-input>
      </el-form-item>
      <el-form-item label="Description" prop="description">
        <el-input type="textarea" v-model="templateForm.description"></el-input>
      </el-form-item>
      <el-form-item label="Keywords" prop="keywords">
        <el-input-tag v-model="templateForm.keywords"></el-input-tag>
      </el-form-item>
      <el-form-item label="Category" prop="categoryId">
        <el-select v-model="templateForm.categoryId" placeholder="Select category" filterable>
          <el-option
            v-for="category in choiceCategoryList"
            :key="category.id"
            :label="category.title"
            :value="category.id"
          ></el-option>
        </el-select>
        <el-button
          type="info"
          icon="el-icon-plus"
          circle
          size="small"
          @click="handleAddCategory"
        ></el-button>
      </el-form-item>

      <div v-if="me.is_staff">
        <el-divider></el-divider>
        <h3>Staff options</h3>
        <el-form-item prop="isGlobal">
          <el-checkbox
            v-model="templateForm.isGlobal"
            label="Is global choice template"
            :disabled="!isNaN(templateForm.id)"
          ></el-checkbox>
          <help-text v-if="!isNaN(templateForm.id)"
            >(Disabled: this can only be set on new choice templates)</help-text
          >
          <help-text
            >Should this choice template be available to all companies (green template)</help-text
          >
        </el-form-item>
      </div>

      <el-divider></el-divider>

      <h4>Choices</h4>

      <div class="choice-container">
        <div
          class="choice"
          :class="choiceTemplateDialogIsScale ? choiceScaleClass(choice) : 'unpolarized'"
          v-for="choice in choiceTemplateDialogChoices"
          :key="choice.id"
        >
          <span class="choice-title">{{ choice.title }}</span>
        </div>
      </div>
    </el-form>
    <span slot="footer" class="dialog-footer">
      <el-button @click="handleCancel">Cancel</el-button>
      <el-button type="primary" @click="handleSave">{{
        templateForm.id ? 'Save' : 'Create'
      }}</el-button>
    </span>
  </el-dialog>
</template>

<script>
import { mapState } from 'vuex';
import { ElInputTag } from 'el-input-tag';

import HelpText from '@/components/common/HelpText';

import * as surveyBuilderTypes from '@/store/modules/surveybuilder/mutation-types';

export default {
  name: 'ChoiceTemplateSaveDialog',
  components: {
    HelpText,
    ElInputTag,
  },
  data: () => ({
    templateForm: {},
    rules: {
      title: [
        { required: true, message: 'Please enter title of the question', trigger: 'blur' },
        { min: 3, message: 'Length should be a minimum of 3 characters', trigger: 'blur' },
      ],
    },
  }),
  computed: {
    ...mapState('surveybuilder', [
      'choiceCategoryList',
      'choiceTemplateDialogChoices',
      'choiceTemplateDialogIsScale',
      'choiceTemplateDialogForm',
    ]),
    ...mapState('user', ['me']),

    choiceTemplate() {
      return { ...this.templateForm, choices: this.choiceTemplateDialogChoices };
    },

    isVisible: {
      get() {
        return this.$store.state.surveybuilder.choiceTemplateDialogVisible;
      },
      set(value) {
        this.$store.commit(
          `surveybuilder/${surveyBuilderTypes.SET_CHOICE_TEMPLATE_DIALOG_VISIBILITY}`,
          value,
        );
      },
    },
  },
  mounted() {
    this.initForm();
  },
  methods: {
    initForm() {
      this.templateForm = { ...this.choiceTemplateDialogForm };
    },

    choiceScaleClass(choice) {
      const polarity = choice.polarity || 0;
      if (polarity > 0) {
        return 'positive';
      } else if (polarity < 0) {
        return 'negative';
      }
      return 'neutral';
    },

    resetForm() {
      this.$store.commit(`surveybuilder/${surveyBuilderTypes.RESET_CHOICE_TEMPLATE_DIALOG_FORM}`);
      this.$refs.templateForm.resetFields();
    },

    handleCancel() {
      this.$store.dispatch('surveybuilder/choiceTemplateDialogCancel');
      this.resetForm();
    },

    handleSave() {
      this.$refs.templateForm.validate(valid => {
        if (valid) {
          if (this.templateForm.id) {
            this.handleEdit();
          } else {
            this.handleCreate();
          }
        }
      });
    },

    handleEdit() {
      this.$store.dispatch('surveybuilder/choiceTemplateDialogEdit', this.templateForm).then(() => {
        this.resetForm();
        this.isVisible = false;
      });
    },

    handleCreate() {
      this.$store.dispatch('surveybuilder/choiceTemplateDialogCreate', this.choiceTemplate);
      this.resetForm();
    },

    handleAddCategory() {
      this.$store.commit(
        `surveybuilder/${surveyBuilderTypes.SET_CHOICE_CATEGORY_DIALOG_VISIBILITY}`,
        true,
      );
    },
  },
  watch: {
    choiceTemplateDialogForm() {
      this.initForm();
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../../assets/style/_variables_live.scss';

.choice {
  width: 100%;
  margin-bottom: 4px;
  padding: 8px;
  border-radius: 3px;
  background-color: $colony-light-gray;
  border-color: darken($colony-light-gray, 15%);
}

.positive.choice {
  background-color: lighten($colony-green, 35%);
  border: 1px solid $colony-green;
}

.neutral.choice {
  background-color: lighten($colony-orange, 35%);
  border: 1px solid $colony-orange;
}

.negative.choice {
  background-color: lighten($colony-red, 25%);
  border: 1px solid $colony-red;
}
</style>



// WEBPACK FOOTER //
// ChoiceTemplateSaveDialog.vue?3d291e8e